import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { UserRole } from '../lib/roleUtils';

const DISCGO_SITE_URL = import.meta.env.VITE_DISCGO_SITE_URL || 'http://localhost:3000';

interface AuthContextType {
 user: User | null;
 session: Session | null;
 loading: boolean;
 roles: UserRole[];
 activeRole: UserRole | null;
 profileFirstName: string;
 profileLastName: string;
 setActiveRole: (role: UserRole) => Promise<void>;
 signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
 const [user, setUser] = useState<User | null>(null);
 const [session, setSession] = useState<Session | null>(null);
 const [loading, setLoading] = useState(true);
 const [roles, setRoles] = useState<UserRole[]>([]);
 const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);
 const [profileFirstName, setProfileFirstName] = useState<string>('');
 const [profileLastName, setProfileLastName] = useState<string>('');

 const loadUserSession = async (userId: string | null) => {
 if (!userId) {
 setRoles([]);
 setActiveRoleState(null);
 setProfileFirstName('');
 setProfileLastName('');
 return;
 }

 try {
 const rolesResponse = await supabase
 .from('user_roles')
 .select('role')
 .eq('user_id', userId);

 const profileResponse = await supabase
 .from('profiles')
 .select('active_role, first_name, last_name')
 .eq('id', userId)
 .single();

 const userRoles = (rolesResponse.data ?? []).map((r: any) => r.role as UserRole);
 const userActiveRole = (profileResponse.data as any)?.active_role ?? null;

 setRoles(userRoles);
 setActiveRoleState(userActiveRole as UserRole | null);
 setProfileFirstName((profileResponse.data as any)?.first_name?.trim() || '');
 setProfileLastName((profileResponse.data as any)?.last_name?.trim() || '');

 const hasOwnerAccess = userRoles.includes('owner') || userRoles.includes('admin');
 if (!hasOwnerAccess) {
 console.error('User does not have owner/admin role');
 window.location.href = `${DISCGO_SITE_URL}/login`;
 }
 } catch (error) {
 console.error('Error loading user session:', error);
 setRoles([]);
 setActiveRoleState(null);
 }
 };

 useEffect(() => {
 const initAuth = async () => {
 // Process cross-origin session tokens passed as query params from discgo.golf.
 // We use query params (not hash) so Supabase's detectSessionInUrl doesn't
 // intercept them — which would consume the hash before our code runs.
 const params = new URLSearchParams(window.location.search);
 const accessToken = params.get('sb_access_token');
 const refreshToken = params.get('sb_refresh_token');
 if (accessToken && refreshToken) {
 await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
 window.history.replaceState({}, document.title, window.location.pathname);
 }

 const { data: { session } } = await supabase.auth.getSession();
 setSession(session);
 setUser(session?.user ?? null);
 await loadUserSession(session?.user?.id ?? null);
 setLoading(false);
 };

 initAuth();

 const {
 data: { subscription },
 } = supabase.auth.onAuthStateChange((_event, session) => {
 setSession(session);
 setUser(session?.user ?? null);
 if (session?.user?.id) {
 loadUserSession(session.user.id);
 } else {
 setRoles([]);
 setActiveRoleState(null);
 }
 });

 return () => subscription.unsubscribe();
 }, []);

 const signOut = async () => {
 await supabase.auth.signOut({ scope: 'local' });
 Object.keys(localStorage).forEach(key => {
 if (key.startsWith('sb-')) localStorage.removeItem(key);
 });
 window.location.href = `${DISCGO_SITE_URL}/login?signout=1`;
 };

 const setActiveRole = async (role: UserRole) => {
 if (!user?.id) return;

 if (!roles.includes(role)) {
 console.error(`Cannot set active role to ${role}: user does not possess this role`);
 return;
 }

 const { error } = await supabase
 .from('profiles')
 .update({ active_role: role } as any)
 .eq('id', user.id);

 if (error) {
 console.error('Error updating active role:', error);
 return;
 }

 setActiveRoleState(role);
 };

 const value = {
 user,
 session,
 loading,
 roles,
 activeRole,
 profileFirstName,
 profileLastName,
 setActiveRole,
 signOut,
 };

 return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
 const context = useContext(AuthContext);
 if (context === undefined) {
 throw new Error('useAuth must be used within an AuthProvider');
 }
 return context;
}
